/**
 * Jetons de l'identite visuelle du back-office.
 *
 * **Source des couleurs : `mobile/src/shared/design/theme.ts`** (`lightAppTheme`
 * et `darkAppTheme`, champ `colors`), elle-meme alimentee par
 * `mobile/src/shared/design/tokens.ts` (`brandColors`). Les maquettes du
 * back-office (Figma, bibliotheque « Composants BO », principe B1 n°8) sont
 * construites sur les **memes variables** que l'application : un parent et un
 * exploitant voient la meme marque, et une correction de contraste faite d'un
 * cote vaut de l'autre.
 *
 * **Recopie, et non import — choix assume.** Le fichier du mobile importe
 * `react-native` (`Platform`) : l'importer ici tirerait React Native dans la
 * construction Vite, et un paquet partage entre les deux applications serait
 * disproportionne pour quarante valeurs. La recopie est donc verrouillee par un
 * test de parite (`tokens.parity.test.ts`) qui lit le fichier du mobile **comme
 * du texte** et echoue des qu'une valeur, un nom ou le nombre de jetons diverge.
 * Changer une couleur se fait donc cote mobile d'abord, puis ici.
 *
 * Les justifications chiffrees de chaque valeur (contrastes, chaleur des
 * neutres, corail assombri) vivent a cote de la valeur, dans le fichier du
 * mobile : les recopier ici ferait deux textes a tenir a jour. Une seule regle
 * est rappelee parce qu'elle protege d'une « correction » tentante :
 *
 * **`primary` clair vaut `#C93250`, pas le corail de la charte `#FF8FA3`.** Le
 * corail ne tient qu'environ 2,3:1 sous du texte blanc ; la teinte assombrie
 * tient le 4,5:1 exige d'un libelle de bouton (ADR 0024, decision deja appliquee
 * par le mobile). Le corail reste une couleur de surface, jamais d'encre.
 *
 * Ce fichier et `theme.ts` sont les **seuls** du back-office autorises a ecrire
 * une couleur litterale (`noHexLiteral.test.ts`) : un ecran ou un composant lit
 * le theme, sans quoi le mode sombre ne s'appliquerait pas a cette zone.
 */

/** Noms des jetons de couleur, identiques a `ThemeColors` du mobile. */
export interface ColorTokens {
  readonly background: string;
  readonly surface: string;
  readonly surfaceMuted: string;
  readonly textStrong: string;
  readonly textMuted: string;
  readonly textOnPrimary: string;
  readonly textOnAccent: string;
  readonly border: string;
  readonly primary: string;
  readonly primarySoft: string;
  /** Aplat de charte : surface uniquement, jamais une encre (voir le mobile). */
  readonly secondary: string;
  /** Aplat de charte : surface uniquement, jamais une encre (voir le mobile). */
  readonly success: string;
  readonly successSoft: string;
  readonly successText: string;
  /** Seuil des objets graphiques (3:1) : un filet, une icone — pas un texte. */
  readonly warning: string;
  readonly warningText: string;
  readonly danger: string;
  readonly dangerSoft: string;
  /** Surface uniquement en theme clair : l'encre bleue est `infoText`. */
  readonly info: string;
  readonly infoSoft: string;
  readonly infoText: string;
  readonly critical: string;
  readonly criticalSoft: string;
  readonly criticalText: string;
  /** Aplat plein du cran le plus grave ; son encre est `textOnCritical`. */
  readonly criticalStrong: string;
  readonly textOnCritical: string;
  readonly accentPinkSoft: string;
  readonly accentPinkStrong: string;
  readonly accentMintSoft: string;
  readonly accentMintStrong: string;
  readonly accentBlueSoft: string;
  readonly accentBlueStrong: string;
  readonly accentYellowSoft: string;
  readonly accentYellowStrong: string;
  readonly accentOverlay: string;
  readonly neutralSoft: string;
  readonly backdrop: string;
}

/** `lightAppTheme.colors` du mobile, `brandColors` resolus. */
export const lightColors: ColorTokens = {
  background: '#F3ECEE',
  surface: '#FFFFFF',
  surfaceMuted: '#F6EFF1',
  textStrong: '#2D3748',
  textMuted: '#5B5F66',
  textOnPrimary: '#FFFFFF',
  textOnAccent: '#2D3748',
  border: '#E7DFE2',
  primary: '#C93250',
  primarySoft: '#FFF0F3',
  secondary: '#8EC5FF',
  success: '#A8D5C2',
  successSoft: '#DDEFE7',
  successText: '#2E6B55',
  warning: '#B0760B',
  warningText: '#8A5C00',
  danger: '#D94862',
  dangerSoft: '#FFE8EC',
  info: '#8EC5FF',
  infoSoft: '#DCEEFF',
  infoText: '#285F96',
  critical: '#D01A2B',
  criticalSoft: '#FFE1E3',
  criticalText: '#9B0F1C',
  criticalStrong: '#9B0F1C',
  textOnCritical: '#FFFFFF',
  accentPinkSoft: '#FFE8ED',
  accentPinkStrong: '#C42E4B',
  accentMintSoft: '#E4F4ED',
  accentMintStrong: '#2E6B55',
  accentBlueSoft: '#E4F1FF',
  accentBlueStrong: '#285F96',
  accentYellowSoft: '#FFF3D5',
  accentYellowStrong: '#8F600E',
  accentOverlay: 'rgba(255,255,255,0.72)',
  neutralSoft: '#F6EFF1',
  backdrop: 'rgba(38, 50, 64, 0.45)',
};

/** `darkAppTheme.colors` du mobile : neutres chauds, rose d'action sature. */
export const darkColors: ColorTokens = {
  background: '#0F0C0D',
  surface: '#201B1D',
  surfaceMuted: '#2A2426',
  textStrong: '#F7F2F3',
  textMuted: '#B0A6A9',
  textOnPrimary: '#3A1015',
  textOnAccent: '#2D3748',
  border: '#3E363A',
  primary: '#FF7A96',
  primarySoft: '#34212A',
  secondary: '#8EC5FF',
  success: '#A8D5C2',
  successSoft: '#1A2A25',
  successText: '#7FE0B8',
  warning: '#FFC86F',
  warningText: '#FFC86F',
  danger: '#FF9AA8',
  dangerSoft: '#41212A',
  info: '#93C3FF',
  infoSoft: '#182534',
  infoText: '#93C3FF',
  critical: '#FF6B75',
  criticalSoft: '#45161C',
  criticalText: '#FFB3BA',
  criticalStrong: '#FF6B75',
  textOnCritical: '#3A0A10',
  accentPinkSoft: '#34212A',
  accentPinkStrong: '#FFAABE',
  accentMintSoft: '#1A2A25',
  accentMintStrong: '#7FE0B8',
  accentBlueSoft: '#182534',
  accentBlueStrong: '#93C3FF',
  accentYellowSoft: '#2D2518',
  accentYellowStrong: '#FFD07C',
  accentOverlay: 'rgba(247, 242, 243, 0.10)',
  neutralSoft: '#2A2426',
  backdrop: 'rgba(0, 0, 0, 0.68)',
};

/**
 * Ombres en deux couches (contact + diffusion), reprises des crans `raised` et
 * `overlay` du mobile dans leur dialecte web (`boxShadow`). Le back-office n'a
 * besoin que de ces deux hauteurs : une carte posee, une boite de dialogue.
 */
export const elevation = {
  light: {
    raised: '0px 1px 2px rgba(38, 50, 64, 0.06), 0px 4px 12px rgba(38, 50, 64, 0.10)',
    overlay: '0px 4px 8px rgba(38, 50, 64, 0.12), 0px 18px 40px rgba(38, 50, 64, 0.26)',
  },
  dark: {
    raised: '0px 1px 2px rgba(0, 0, 0, 0.40), 0px 4px 14px rgba(0, 0, 0, 0.56)',
    overlay: '0px 4px 10px rgba(0, 0, 0, 0.56), 0px 18px 42px rgba(0, 0, 0, 0.78)',
  },
} as const;

/**
 * Rayons du back-office, en pixels : ceux de la maquette (champ et bouton 10,
 * tableau 12, carte 16, dialogue 20 ; `field` est le rayon 14 du grand champ
 * de l'ecran de connexion). Ils different de l'echelle du mobile, qui
 * dessine pour le doigt et non pour un pointeur ; le test de parite ne porte que
 * sur les couleurs.
 */
export const radius = { control: 10, field: 14, table: 12, card: 16, dialog: 20, pill: 999 } as const;

/**
 * Hauteurs de controle : 40 px pour la taille M, cible de pointage confortable
 * d'un outil de bureau ; 30 px pour la taille S, reservee aux lignes de tableau
 * et aux barres d'outils denses (WCAG 2.2, 2.5.8 : 24 px minimum) ; 56 px pour
 * la taille L, le grand champ des ecrans de connexion et de recuperation.
 */
export const controlHeight = { L: 56, M: 40, S: 30 } as const;

/** Cible tactile minimale sur un ecran etroit (sous 600 px). */
export const touchTarget = 44;

/**
 * Graisses embarquees : les cinq de l'application (ADR 0023). Nunito Black (900)
 * n'est pas embarquee — la maquette l'emploie pour un titre, ExtraBold la
 * remplace partout (ecart assume, issue #498).
 */
export const fontWeights = { regular: 400, medium: 500, semiBold: 600, bold: 700, extraBold: 800 } as const;

export const fontStacks = {
  brand:
    '"Nunito", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  /** Identifiants, codes et cles techniques : on les relit caractere par caractere. */
  mono: 'ui-monospace, SFMono-Regular, "Roboto Mono", Menlo, Consolas, monospace',
} as const;

/**
 * Largeur sous laquelle un tableau se replie en cartes empilees. Egale au point
 * de rupture `sm` de Material UI, d'ou l'emploi de `breakpoints.down('sm')`.
 */
export const STACKED_TABLE_BELOW_PX = 600;
